'use client'

import { useTranslations } from 'next-intl'
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent
} from 'react'

import IconArrowLeft from 'asset/icon/arrow_left.svg'
import SearchIcon from 'asset/icon/search.svg'
import Link from 'component/block/link'
import TextAreaAutoResize from 'component/block/textAreaAutoResize'
import SearchQueryCard from 'component/page/search/dropdown-card/query'
import RecentCard from 'component/page/search/dropdown-card/recent'
import useClickOutside from 'hook/useClickOutside'
import { useRouter } from 'lib/next-intl'
import { useRecentSearchStore } from 'store/recent-search'
import { TabKey } from 'type/search'
import { twc } from 'util/cn'
import debounce from 'util/debounce'

const SearchDropdown = twc.div`fixed top-[calc(var(--fixed-top)_+_64px)] isolate z-[2] w-full min360:max-w-[260px] min480:max-w-[360px] min600:max-w-[600px] overflow-hidden rounded-[12px] bg-background-1st py-[12px] shadow-floating-dropdown playsee-mb:bottom-[calc(var(--cookie-hint-height)_+_96px)] playsee-mb:left-0 playsee-mb:max-w-none playsee-mb:rounded-none playsee-mb:shadow-none`

const MemoizedTextAreaAutoResize = memo(TextAreaAutoResize)

interface IDropDownMenu {
  defaultValue?: string
}
const SearchBox = ({ defaultValue = '' }: IDropDownMenu) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const t = useTranslations()
  const [showCard, setShowCard] = useState(false)
  const [searchText, setSearchText] = useState(defaultValue || '')
  const [debounceSearch, setDebounceSearch] = useState(searchText)
  const addSearch = useRecentSearchStore(state => state.addSearch)

  const { push } = useRouter()

  const onFocusHandler = useCallback(() => {
    setShowCard(true)
  }, [])

  const onClearInput = useCallback(() => {
    setDebounceSearch('')
    setSearchText('')
    textAreaRef.current?.focus()
  }, [])

  const onScrollList = useCallback(() => {
    textAreaRef.current?.blur()
  }, [])

  const debounceUpdate = useMemo(
    () =>
      debounce(1000, (value: string) => {
        setDebounceSearch(value)
      }),
    []
  )

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setSearchText(value)
    debounceUpdate(value)
  }

  const onClose = () => {
    setShowCard(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (searchText.length > 0) {
        const searchParams = new URLSearchParams()
        searchParams.set('q', searchText)
        searchParams.set('tab', TabKey.Spot)

        addSearch({
          type: 'keyword',
          keyword: searchText,
          id: searchText
        })
        push(`/search?${searchParams.toString()}`)
      }
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const threshold = 200

      if (scrollY > threshold) {
        setShowCard(false)
        textAreaRef.current?.blur()
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    const textarea = textAreaRef.current
    if (showCard && textarea) {
      textarea.focus()
      const valueLength = textAreaRef.current.value.length
      textarea.setSelectionRange(0, valueLength)
    }
  }, [showCard])

  useClickOutside(wrapperRef, onClose)

  const showRecent = searchText.length === 0

  return (
    <div className='sticky top-[--fixed-top] z-[3] w-full'>
      <div
        ref={wrapperRef}
        className='bg-background-1st py-[12px] playsee-mb:px-[12px]'
      >
        <div className='flex items-center justify-center'>
          {defaultValue && (
            <Link
              href='/search'
              className='mr-[8px] flex-shrink-0 cursor-pointer'
            >
              <IconArrowLeft width={20} height={20} />
            </Link>
          )}

          <MemoizedTextAreaAutoResize
            ref={textAreaRef}
            showClear
            value={searchText}
            placeholder={t('common.search')}
            className='flex-shrink flex-grow'
            textareaClassName='h-[26px] overflow-hidden whitespace-nowrap'
            maxRows={1}
            prefix={<SearchIcon className='h-[18px] w-[18px] fill-label-l1' />}
            onFocus={onFocusHandler}
            onChange={handleTextChange}
            onClear={onClearInput}
            onKeyDown={handleKeyDown}
          />
          {showCard && (
            <div
              className='hidden flex-shrink-0 pl-[8px] font-semibold text-label-l1 playsee-mb:block'
              onClick={onClose}
            >
              {t('common.cancel')}
            </div>
          )}
        </div>

        {showCard && (
          <SearchDropdown onScroll={onScrollList}>
            {showRecent ? (
              <RecentCard />
            ) : (
              <SearchQueryCard
                keyword={searchText}
                debounceSearch={debounceSearch}
              />
            )}
          </SearchDropdown>
        )}
      </div>
    </div>
  )
}

export default SearchBox
